import React, { useState } from 'react';
import axios from 'axios';
import { Box, Paper, Grid, Stack, TextField, Button, Typography } from '@mui/material';
import { ArrowForwardIos, ContentCopy, VpnKey } from '@mui/icons-material';
import Notification from '../Notification';
import { useNavigate } from 'react-router-dom';

function GenerateApiKeyForm({ onGenerated }) {
  const [name, setName] = useState('Finance Console');
  const [error, setError] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/company-master/api-keys`,
        { name: name.trim() }
      );
      if (response.status === 200 || response.status === 201) {
        setGeneratedKey(response.data.api_key || '');
        setNotify({
          isOpen: true,
          message: response.data.message || 'API key generated successfully',
          type: 'success',
        });
        if (onGenerated) {
          onGenerated();
        }
      }
    } catch (e) {
      if (e.response && e.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error',
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setNotify({
          isOpen: true,
          message: e.response?.data?.error_message || 'Failed to generate API key',
          type: 'error',
        });
      }
    }
    setSubmitting(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedKey);
      setNotify({
        isOpen: true,
        message: 'API key copied to clipboard',
        type: 'success',
      });
    } catch (err) {
      setNotify({
        isOpen: true,
        message: 'Could not copy. Select the key and copy it manually.',
        type: 'warning',
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', margin: '-15px', padding: '30px', fontWeight: '500px' }}>
      <Paper elevation={3} sx={{ width: '600px', marginTop: '-10px' }}>
        <Grid className="form-title" container spacing={2}>
          <Grid item>
            <Typography variant="h5" sx={{ textAlign: 'center', margin: 2 }}>
              Generate API Key
            </Typography>
          </Grid>
          <Grid item>
            <VpnKey sx={{ width: 40, height: 40, marginLeft: '-20px' }} />
          </Grid>
        </Grid>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={3} sx={{ margin: '30px' }}>
            <TextField
              variant="outlined"
              name="name"
              label={
                <span>
                  Key Name<span style={{ color: 'red', marginLeft: '3px' }}>*</span>
                </span>
              }
              size="small"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError('');
              }}
              disabled={!!generatedKey}
              {...(error && { error: true, helperText: error })}
            />

            {!generatedKey && (
              <Grid container justifyContent="flex-end">
                <Grid item>
                  <Button
                    className="form-btn"
                    variant="contained"
                    type="submit"
                    disabled={submitting}
                    endIcon={<ArrowForwardIos sx={{ width: 18, height: 18 }} />}
                  >
                    Generate
                  </Button>
                </Grid>
              </Grid>
            )}

            {generatedKey && (
              <>
                <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 600 }}>
                  Copy this key now. It will not be shown again.
                </Typography>
                <TextField
                  variant="outlined"
                  label="API Key"
                  size="small"
                  value={generatedKey}
                  InputProps={{ readOnly: true }}
                  multiline
                />
                <Button
                  className="form-btn"
                  variant="outlined"
                  onClick={handleCopy}
                  startIcon={<ContentCopy />}
                  sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                >
                  Copy key
                </Button>
              </>
            )}
          </Stack>
        </form>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default GenerateApiKeyForm;
